"use client";

import { useClimateState } from "./use-climate-state";
import { ShareTools } from "./share-tools";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MARINE_HEATWAVES,
  PATHWAYS,
  SEA_LEVEL,
  SEA_TEMPERATURE,
  WARMING,
} from "@/lib/climate/evidence";
import {
  DataTable,
  RangeChart,
  SectionTitle,
  Segments,
  Sources,
  useChartAnimation,
} from "./ui";
import s from "./climate.module.css";

export function GlobalOutlook() {
  const [pathway, setPathway] = useClimateState("pathway", "middle", [
    "low",
    "middle",
    "high",
  ]);
  const [period, setPeriod] = useClimateState("period", "2090", [
    "2050",
    "2090",
  ]);
  const animate = useChartAnimation();
  const active = PATHWAYS.find((p) => p.key === pathway)!;
  const estimate = WARMING[pathway][period];
  const data = (["2050", "2090"] as const).map((year) => ({
    period: year === "2050" ? "2041–2060" : "2081–2100",
    low: WARMING.low[year].value,
    middle: WARMING.middle[year].value,
    high: WARMING.high[year].value,
    range: [WARMING[pathway][year].low, WARMING[pathway][year].high],
  }));
  return (
    <section className={s.hero} id="global">
      <div className={s.wrap}>
        <div className={s.topline}>
          <div className={s.jump}>
            <a href="#global">◎ Global outlook</a>
            <a href="#compare">Compare places</a>
            <a href="#your-location">Your location ↓</a>
          </div>
          <span className={s.reviewed}>
            Evidence reviewed · 11 October 2026
          </span>
        </div>
        <div className={s.intro}>
          <div>
            <div className={`${s.eyebrow} mt-8`}>
              PeakWave climate observatory
            </div>
            <h1>
              The future of
              <br />
              our seasons.
            </h1>
          </div>
          <p>
            Less reliable snow. Warmer seas. Changing swells. Explore what
            scientists project, and what it could mean for the places and sports
            we love.
          </p>
        </div>
        <div className={s.heroChart}>
          <div className={s.chartHead}>
            <h2>A warmer world. Different winters.</h2>
            <Segments
              label="Warming projection period"
              value={period}
              onChange={setPeriod}
              options={[
                { value: "2050", label: "2041–2060" },
                { value: "2090", label: "2081–2100" },
              ]}
            />
          </div>
          <div className={s.heroGrid}>
            <div>
              <p className={s.small}>
                Global warming relative to 1850–1900 · °C
              </p>
              <div
                className={s.chart}
                role="img"
                aria-label={`Global warming pathways. ${active.label}: ${estimate.value} degrees, very likely range ${estimate.low} to ${estimate.high}. Full values in chart data below.`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={data}
                    margin={{ top: 22, right: 34, bottom: 12, left: -18 }}
                    accessibilityLayer
                  >
                    <CartesianGrid stroke="#8194aa35" vertical={false} />
                    <XAxis
                      dataKey="period"
                      stroke="#bccbd9"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      padding={{ left: 30, right: 20 }}
                    />
                    <YAxis
                      domain={[0, 5]}
                      ticks={[0, 1, 2, 3, 4, 5]}
                      stroke="#bccbd9"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      unit="°"
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#16233B",
                        border: "1px solid #64748b",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 12,
                      }}
                      formatter={(value, name) => [
                        Array.isArray(value)
                          ? `${value.join("–")}°C`
                          : `${value}°C`,
                        name === "range"
                          ? `${active.code} very likely range`
                          : (PATHWAYS.find((p) => p.key === name)?.code ??
                            name),
                      ]}
                    />
                    <Area
                      type="linear"
                      dataKey="range"
                      stroke="none"
                      fill={active.colour}
                      fillOpacity={0.16}
                      isAnimationActive={animate}
                    />
                    {PATHWAYS.map((p) => (
                      <Line
                        key={p.key}
                        type="linear"
                        dataKey={p.key}
                        stroke={p.colour}
                        strokeWidth={p.key === pathway ? 3 : 1.5}
                        strokeOpacity={p.key === pathway ? 1 : 0.65}
                        dot={{ r: p.key === pathway ? 5 : 3, fill: p.colour }}
                        isAnimationActive={animate}
                      />
                    ))}
                    <ReferenceDot
                      x={period === "2050" ? "2041–2060" : "2081–2100"}
                      y={estimate.value}
                      r={8}
                      fill={active.colour}
                      stroke="white"
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div
                className={s.pathways}
                role="group"
                aria-label="Global emissions pathway"
              >
                {PATHWAYS.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    aria-pressed={p.key === pathway}
                    onClick={() => setPathway(p.key)}
                  >
                    <span className={s.dot} style={{ background: p.colour }} />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={s.heroAside} aria-live="polite">
              <div className={s.small}>
                {active.code} · {period === "2050" ? "2041–2060" : "2081–2100"}
              </div>
              <div className={s.bigNumber} style={{ color: active.colour }}>
                +{estimate.value.toFixed(1)}°
              </div>
              <p>
                <strong>
                  {estimate.low.toFixed(1)}–{estimate.high.toFixed(1)}°C
                </strong>{" "}
                very likely range
              </p>
              <p className="mt-5">
                {active.description} The shaded band shows uncertainty within
                this pathway.
              </p>
              <p className="mt-4">
                These are 20-year averages. Connecting lines guide the eye; they
                are not annual forecasts.
              </p>
            </div>
          </div>
          <DataTable
            headers={["Pathway", "2041–2060 °C", "2081–2100 °C"]}
            rows={PATHWAYS.map((p) => [
              p.code,
              ...(["2050", "2090"] as const).map((year) => {
                const n = WARMING[p.key][year];
                return `${n.value} (${n.low}–${n.high})`;
              }),
            ])}
          />
          <Sources ids={["warming"]} />
          <ShareTools chart="warming" />
          <p className={s.small}>
            Lower and higher emissions are conditional futures, not guaranteed
            best and worst cases. No probability is assigned to these pathways.
          </p>
          <div className={s.policyStrip}>
            <strong>~2.8°C</strong>
            <p>
              UNEP’s 2025 assessment of current policies over this century. A
              useful reality check, assessed separately from the IPCC pathways
              above.
            </p>
            <a href="#choices" className={s.small}>
              What changes the outcome? ↓
            </a>
          </div>
          <Sources ids={["policy"]} />
        </div>
      </div>
    </section>
  );
}

export function OceanOutlook() {
  const [period, setPeriod] = useClimateState("seaYear", "2100", [
    "2050",
    "2100",
  ]);
  const [seaPathway, setSeaPathway] = useClimateState("seaPathway", "middle", [
    "low",
    "middle",
    "high",
  ]);
  const [heatPathway, setHeatPathway] = useClimateState("heat", "low", [
    "low",
    "very-high",
  ]);
  const [temperaturePathway, setTemperaturePathway] = useClimateState(
    "temperature",
    "middle",
    ["low", "middle", "high"],
  );
  const selected = PATHWAYS.findIndex((item) => item.key === seaPathway);
  const heat = heatPathway === "low" ? 0 : 1;
  const temperature = PATHWAYS.findIndex(
    (item) => item.key === temperaturePathway,
  );
  const rows = PATHWAYS.map((p) => ({
    name: p.code,
    ...SEA_LEVEL[p.key][period],
  }));
  const estimate = rows[selected];
  return (
    <section id="ocean" className={s.section}>
      <div className={s.wrap}>
        <SectionTitle
          number="02 / The changing ocean"
          title="The sea we return to is changing."
        >
          Wave quality is only part of the picture. Higher water levels and
          longer spells of unusual heat change coastlines and the life beneath
          the surface.
        </SectionTitle>
        <div className={s.twoCol}>
          <article className={s.panel}>
            <div className={s.chartHead}>
              <h3>Sea level keeps rising.</h3>
              <Segments
                label="Sea-level projection year"
                value={period}
                onChange={setPeriod}
                options={[
                  { value: "2050", label: "2050" },
                  { value: "2100", label: "2100" },
                ]}
              />
            </div>
            <p className={s.small}>
              Global mean rise in centimetres · relative to 1995–2014
            </p>
            <RangeChart
              rows={rows}
              max={100}
              unit="cm"
              selected={selected}
              onSelect={(index) => setSeaPathway(PATHWAYS[index].key)}
              label="Sea-level rise by pathway"
            />
            <p className={s.caption} aria-live="polite">
              {estimate.value} cm median rise by {period}
            </p>
            <p className={s.small}>
              {estimate.name}: likely range {estimate.low}–{estimate.high} cm.
              Bars show ranges; dark marks show medians. Regional sea level can
              differ substantially.
            </p>
            <DataTable
              headers={["Pathway", "Median (cm)", "Likely range (cm)"]}
              rows={rows.map((r) => [r.name, r.value, `${r.low}–${r.high}`])}
            />
            <Sources ids={["sea"]} />
            <p className={s.note}>
              For surf and coastal access: a higher baseline changes water depth
              over reefs and sandbanks and raises flooding exposure. It does not
              mean every tidal range grows.
            </p>
            <Sources ids={["tides"]} />
            <ShareTools chart="sea-level" />
          </article>
          <article className={s.panel}>
            <h3>Marine heatwaves become more frequent.</h3>
            <p className={s.small}>
              Global frequency in 2081–2100 · multiplier versus 1995–2014
            </p>
            <RangeChart
              rows={MARINE_HEATWAVES}
              max={20}
              unit="×"
              selected={heat}
              onSelect={(index) =>
                setHeatPathway(index === 0 ? "low" : "very-high")
              }
              label="Marine heatwave frequency ranges"
            />
            <p className={s.caption} aria-live="polite">
              {MARINE_HEATWAVES[heat].low}–{MARINE_HEATWAVES[heat].high} times
              as frequent
            </p>
            <p className={s.small}>
              Reported projection range. This comparison uses SSP5-8.5, a very
              high emissions pathway, rather than the SSP3-7.0 pathway in the
              global chart.
            </p>
            <DataTable
              headers={["Pathway", "Frequency multiplier"]}
              rows={MARINE_HEATWAVES.map((r) => [
                r.name,
                `${r.low}–${r.high}×`,
              ])}
            />
            <Sources ids={["heat"]} />
            <p className={s.note}>
              For diving, swimming and marine life: warmer averages also bring
              damaging heat extremes. Habitat loss and shifting species can
              change familiar reefs and fisheries.
            </p>
            <Sources ids={["oceanLife"]} />
            <ShareTools chart="marine-heatwaves" />
          </article>
          <article className={`${s.panel} ${s.panelWide}`}>
            <h3>A warmer sea, even between heatwaves.</h3>
            <p className={s.small}>
              Global mean sea-surface warming by 2081–2100 · change relative to
              1995–2014, in °C
            </p>
            <RangeChart
              rows={SEA_TEMPERATURE}
              max={4}
              unit="°C"
              selected={temperature}
              onSelect={(index) => setTemperaturePathway(PATHWAYS[index].key)}
              label="Sea-surface warming by pathway"
              pointLabel="model mean"
            />
            <p className={s.caption} aria-live="polite">
              +{SEA_TEMPERATURE[temperature].value.toFixed(2)}°C model mean ·{" "}
              {SEA_TEMPERATURE[temperature].name}
            </p>
            <p className={s.small}>
              Bars span the 5–95% model range; dark marks show means. This is
              additional warming from a recent baseline, rather than the
              pre-industrial baseline in the opening chart. Local sea
              temperatures can change differently.
            </p>
            <DataTable
              headers={["Pathway", "Mean warming (°C)", "5–95% range (°C)"]}
              rows={SEA_TEMPERATURE.map((row) => [
                row.name,
                row.value,
                `${row.low}–${row.high}`,
              ])}
            />
            <Sources ids={["temperature"]} />
            <ShareTools chart="sea-temperature" />
          </article>
        </div>
      </div>
    </section>
  );
}
